import React, { useMemo } from "react";
import { formatCurrency } from "../../../../utils/financialMath";
import {
  civilDateTodayInZone,
  compareCivilYmd,
  formatCivilDateMedium,
  normalizeBarTimeZone,
  parseCivilDateOnly,
} from "../../../../utils/civilDateTime.js";

/**
 * Earnings Table Component
 * Excel-like table showing all past shows with earnings data.
 * If onRowClick is provided, data rows are clickable (opens show card for editing).
 */
const EarningsTable = ({ shows, selectedBarId, availableBars, onRowClick }) => {
  // Filter and sort past shows
  const pastShows = useMemo(() => {
    if (!shows || shows.length === 0) return [];

    return shows
      .filter((show) => {
        const tz = normalizeBarTimeZone(show.bar_time_zone);
        const todayYmd = civilDateTodayInZone(tz);
        const showYmd = parseCivilDateOnly(show.date);
        return compareCivilYmd(showYmd, todayYmd) < 0;
      })
      .sort((a, b) => compareCivilYmd(b.date, a.date));
  }, [shows]);

  // Calculate totals for the totals row
  const totals = useMemo(() => {
    return pastShows.reduce(
      (acc, show) => {
        acc.totalBasePay += show.base_pay || 0;
        acc.totalTipsCash += show.tips_cash || 0;
        acc.totalTipsElectronic += show.tips_electronic || 0;
        return acc;
      },
      {
        totalBasePay: 0,
        totalTipsCash: 0,
        totalTipsElectronic: 0,
      }
    );
  }, [pastShows]);

  // Calculate total earnings for totals row
  const totalEarnings = useMemo(() => {
    return totals.totalBasePay + totals.totalTipsCash + totals.totalTipsElectronic;
  }, [totals]);

  // Get bar name for totals row
  const barNameForTotals = useMemo(() => {
    if (selectedBarId === null) {
      return "All Bars";
    }
    const bar = availableBars?.find((b) => b.bar_id === selectedBarId);
    return bar ? bar.name : "All Bars";
  }, [selectedBarId, availableBars]);

  const formatDate = (dateString, barTimeZone) =>
    formatCivilDateMedium(dateString, normalizeBarTimeZone(barTimeZone));

  if (pastShows.length === 0) {
    return (
      <div className="earnings-table-empty">
        <p>No past shows with earnings data.</p>
      </div>
    );
  }

  return (
    <div className="earnings-table-container">
      <table className="earnings-table earnings-tables-aligned">
        <colgroup>
          <col className="earnings-col-date" />
          <col className="earnings-col-bar-name" />
          <col className="earnings-col-base-pay" />
          <col className="earnings-col-cash-tips" />
          <col className="earnings-col-electronic-tips" />
          <col className="earnings-col-total" />
        </colgroup>
        <thead>
          <tr>
            <th>Date</th>
            <th>Bar Name</th>
            <th>Base Pay</th>
            <th>Cash Tips</th>
            <th>Electronic Tips</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {/* Totals Row */}
          <tr className="earnings-table-totals-row">
            <td className="earnings-table-mobile-summary-heading">TOTALS</td>
            <td data-label="Bar Name">{barNameForTotals}</td>
            <td data-label="Base Pay">
              {formatCurrency(totals.totalBasePay)}
            </td>
            <td data-label="Cash Tips">
              {formatCurrency(totals.totalTipsCash)}
            </td>
            <td data-label="Electronic Tips">
              {formatCurrency(totals.totalTipsElectronic)}
            </td>
            <td data-label="Total">{formatCurrency(totalEarnings)}</td>
          </tr>
          {/* Data Rows */}
          {pastShows.map((show, index) => {
            const showTotal = (show.base_pay || 0) + (show.tips_cash || 0) + (show.tips_electronic || 0);
            const isClickable = Boolean(onRowClick);
            return (
              <tr
                key={show.show_id || index}
                className={`${index % 2 === 0 ? "even" : "odd"}${isClickable ? " earnings-table-row-clickable" : ""}`}
                onClick={isClickable ? () => onRowClick(show) : undefined}
                onKeyDown={
                  isClickable
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onRowClick(show);
                        }
                      }
                    : undefined
                }
                role={isClickable ? "button" : undefined}
                tabIndex={isClickable ? 0 : undefined}
              >
                <td data-label="Date">
                  {formatDate(show.date, show.bar_time_zone)}
                </td>
                <td data-label="Bar Name">{show.bar_name || "N/A"}</td>
                <td data-label="Base Pay">
                  {formatCurrency(show.base_pay || 0)}
                </td>
                <td data-label="Cash Tips">
                  {formatCurrency(show.tips_cash || 0)}
                </td>
                <td data-label="Electronic Tips">
                  {formatCurrency(show.tips_electronic || 0)}
                </td>
                <td data-label="Total">{formatCurrency(showTotal)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default EarningsTable;

